import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

/**
 * Google Analytics 4. Loads gtag.js and fires a page_view on every
 * client-side navigation (GA can't see SPA route changes on its own,
 * so automatic page views are disabled and sent manually here).
 *
 * Enabled only in production builds and only when VITE_GA_MEASUREMENT_ID
 * is set at build time (see .env.example). Without it the component is a
 * no-op, so dev servers and forks ship without a tracker.
 */
const GA_ID = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined
const ENABLED = Boolean(GA_ID) && import.meta.env.PROD

declare global {
  interface Window {
    dataLayer: unknown[]
    gtag: (...args: unknown[]) => void
  }
}

let loaded = false

function ensureGtag(id: string) {
  if (loaded) return
  loaded = true

  window.dataLayer = window.dataLayer || []
  // GA reads the Arguments object itself; pushing a rest-args array is
  // silently ignored, hence the old-style function.
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments)
  }
  window.gtag('js', new Date())
  window.gtag('config', id, { send_page_view: false })

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${id}`
  document.head.appendChild(script)
}

function trackPageView(path: string) {
  if (!ENABLED || !GA_ID) return
  ensureGtag(GA_ID)
  window.gtag('event', 'page_view', {
    page_path: path,
    page_location: window.location.origin + path,
    page_title: document.title,
  })
}

export function Analytics() {
  const location = useLocation()

  useEffect(() => {
    trackPageView(location.pathname + location.search)
  }, [location])

  return null
}
